import type {DiscoveryEventKey} from "@/app/actions/trackDiscoveryEventUtils";
import {sendDiscoveryEventBeacon} from "@/lib/discoveryEventBeacon";
import {isPrivacyPolicyPath} from "./isPrivacyPolicyPath";

const DEFAULT_DISCOVERY_BASE_PATH = "/discover";
const EXIT_DEDUPE_MS = 1000;

export const PRIVACY_POLICY_POP_UP_VIEWED_EVENT =
	"privacy_policy_pop_up_viewed" as const satisfies DiscoveryEventKey;
export const OPENED_NEW_PRIVACY_POLICY_EVENT =
	"opened_new_privacy_policy" as const satisfies DiscoveryEventKey;
export const AGREED_NEW_PRIVACY_POLICY_EVENT =
	"agreed_new_privacy_policy" as const satisfies DiscoveryEventKey;
export const EXITED_PRIVACY_POLICY_PAGE_EVENT =
	"exited_privacy_policy_page" as const satisfies DiscoveryEventKey;

type PrivacyPolicyEventKey =
	| typeof PRIVACY_POLICY_POP_UP_VIEWED_EVENT
	| typeof OPENED_NEW_PRIVACY_POLICY_EVENT
	| typeof AGREED_NEW_PRIVACY_POLICY_EVENT
	| typeof EXITED_PRIVACY_POLICY_PAGE_EVENT;

let popupViewTrackedForUrl: string | null = null;
let lastExitUrl = "";
let lastExitAt = 0;

export function resetPrivacyPolicyTrackingForTests(): void {
	popupViewTrackedForUrl = null;
	lastExitUrl = "";
	lastExitAt = 0;
}

/**
 * True when `href` points at a Discovery privacy-policy page. Accepts
 * app-relative paths (`/privacy`) and basePath or absolute URLs
 * (`/discover/privacy`, `https://artofproblemsolving.com/discover/privacy`).
 */
export function isNewPrivacyPolicyHref(href: string): boolean {
	const withoutHash = href.split("#")[0] ?? "";
	const withoutQuery = withoutHash.split("?")[0] ?? "";
	if (!withoutQuery) {
		return false;
	}

	let pathname = withoutQuery;
	if (/^[a-z][a-z0-9+.-]*:/i.test(withoutQuery)) {
		try {
			pathname = new URL(withoutQuery).pathname;
		} catch {
			return false;
		}
	}

	const basePath = (
		process.env.NEXT_PUBLIC_DISCOVERY_BASE_PATH ?? DEFAULT_DISCOVERY_BASE_PATH
	).replace(/\/+$/, "");
	if (
		basePath &&
		(pathname === basePath || pathname.startsWith(`${basePath}/`))
	) {
		pathname = pathname.slice(basePath.length) || "/";
	}

	return isPrivacyPolicyPath(pathname);
}

export function currentPageUrl(): string {
	if (typeof window === "undefined") {
		return "";
	}
	return window.location.href;
}

export function trackPrivacyPolicyEvent(
	event: PrivacyPolicyEventKey,
	url = currentPageUrl()
): void {
	if (!url) {
		return;
	}
	sendDiscoveryEventBeacon(event, {url});
}

/**
 * Fires `privacy_policy_pop_up_viewed` once per page URL while the modal is
 * visible. Hiding the modal clears the dedupe so a later appearance counts.
 */
export function trackPrivacyPolicyPopupViewed(
	visible: boolean,
	url: string
): void {
	if (!visible) {
		popupViewTrackedForUrl = null;
		return;
	}
	if (!url || popupViewTrackedForUrl === url) {
		return;
	}
	popupViewTrackedForUrl = url;
	trackPrivacyPolicyEvent(PRIVACY_POLICY_POP_UP_VIEWED_EVENT, url);
}

/**
 * Fires `opened_new_privacy_policy` when the clicked href is a new privacy
 * policy page. Other announcement links are ignored.
 */
export function trackOpenedNewPrivacyPolicy(href: string): void {
	if (!isNewPrivacyPolicyHref(href)) {
		return;
	}
	trackPrivacyPolicyEvent(OPENED_NEW_PRIVACY_POLICY_EVENT);
}

/** Ignores a second exit for the same URL inside {@link EXIT_DEDUPE_MS}. */
export function trackPrivacyPolicyPageExit(
	url: string,
	now = Date.now()
): boolean {
	if (!url) {
		return false;
	}
	if (lastExitUrl === url && now - lastExitAt < EXIT_DEDUPE_MS) {
		return false;
	}
	lastExitUrl = url;
	lastExitAt = now;
	trackPrivacyPolicyEvent(EXITED_PRIVACY_POLICY_PAGE_EVENT, url);
	return true;
}
